// =============================================
// Pillar Preview Modal (STOP-P3 faza P)
//
// Pokazuje final_html pillara + sidebar z lista linkow w prozie + orphans w footerze + relations count.
// Klik link w sidebar → przewija HTML preview do paragrafu z tym linkiem.
// =============================================

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { parseInlineRelativeLinks, type PillarLinkPostCheckResult } from "@/services/contentEngine/pillarLinkPostCheck";

export interface PillarPreviewModalProps {
  open: boolean;
  pillarNodeId: string;
  pillarTitle: string;
  onClose: () => void;
}

export function PillarPreviewModal({ open, pillarNodeId, pillarTitle, onClose }: PillarPreviewModalProps) {
  const [loading, setLoading] = useState(false);
  const [html, setHtml] = useState<string>("");
  const [postCheck, setPostCheck] = useState<PillarLinkPostCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setHtml("");
      setPostCheck(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: loadErr } = await (supabase as any)
          .from("thematic_node_content")
          .select("final_html, engine_debug")
          .eq("node_id", pillarNodeId)
          .single();

        if (cancelled) return;
        if (loadErr || !data) {
          setError(loadErr?.message || "Brak content");
          return;
        }
        setHtml((data.final_html as string) || "");
        // Try to extract pillar.post_check z engine_debug
        const dbg = data.engine_debug as { pillar?: { post_check?: PillarLinkPostCheckResult } } | null;
        if (dbg?.pillar?.post_check) {
          setPostCheck(dbg.pillar.post_check);
        } else {
          setPostCheck(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [open, pillarNodeId]);

  // Parse links from current HTML (fallback gdy postCheck brak)
  const linksInProse = useMemo(() => {
    if (postCheck?.links_in_prose) return postCheck.links_in_prose;
    if (!html) return [];
    const parsed = parseInlineRelativeLinks(html);
    const counts = new Map<string, number>();
    for (const p of parsed) counts.set(p.slug, (counts.get(p.slug) ?? 0) + 1);
    return [...counts.entries()].map(([slug, count]) => ({ slug, count, first_occurrence_kept: true }));
  }, [html, postCheck]);

  const orphans = postCheck?.orphans ?? [];
  const relationsRefreshed = postCheck?.node_relations_refreshed ?? 0;

  const scrollToSlug = (slug: string) => {
    if (!previewRef.current) return;
    const anchors = previewRef.current.querySelectorAll(`a[href="${slug}"]`);
    if (anchors.length > 0) {
      (anchors[0] as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" });
      (anchors[0] as HTMLElement).style.outline = "2px solid orange";
      setTimeout(() => { (anchors[0] as HTMLElement).style.outline = ""; }, 2000);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Podglad pillar page</DialogTitle>
          <DialogDescription>
            <span className="font-medium">{pillarTitle}</span>
            <br />
            <span className="text-xs text-muted-foreground">
              {linksInProse.length} linkow w prozie · {orphans.length} w footerze · {relationsRefreshed} relations
            </span>
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-3 text-sm">Ladowanie pillara...</span>
          </div>
        )}

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
            <p className="font-medium text-destructive">Blad</p>
            <p className="text-xs font-mono">{error}</p>
          </div>
        )}

        {!loading && !error && html && (
          <div className="grid grid-cols-[1fr_280px] gap-4 flex-1 overflow-hidden">
            {/* HTML preview */}
            <div
              ref={previewRef}
              className="overflow-y-auto rounded-md border bg-card p-6 prose prose-sm max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: html }}
            />

            {/* Sidebar: links + orphans + relations */}
            <div className="overflow-y-auto space-y-4 pr-1">
              <div className="rounded-md border bg-muted/30 p-3">
                <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">
                  Linki w prozie ({linksInProse.length})
                </p>
                {linksInProse.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Brak linkow.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {linksInProse.map((l) => (
                      <li key={l.slug} className="flex items-start gap-2">
                        <button
                          onClick={() => scrollToSlug(l.slug)}
                          className="text-xs font-mono text-left hover:underline truncate flex-1"
                          title={l.slug}
                        >
                          {l.slug}
                        </button>
                        {l.count > 1 && (
                          <Badge variant="outline" className="text-[10px]">
                            x{l.count}
                          </Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {orphans.length > 0 && (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                  <p className="text-xs uppercase tracking-wide font-semibold text-amber-700 mb-1">
                    Sieroty ({orphans.length}) — diagnostyka
                  </p>
                  <p className="text-[10px] text-muted-foreground mb-2">
                    Spoki published, ktore NIE trafily do prozy. NIE renderowane w HTML (P-B). Sygnal ze outline LLM lub matching tytulow zawiodl.
                  </p>
                  <ul className="space-y-1.5">
                    {orphans.map((o) => (
                      <li key={o.slug} className="flex items-start gap-1.5">
                        <ExternalLink className="h-3 w-3 mt-0.5 shrink-0 text-amber-600" />
                        <span className="text-xs truncate" title={o.title}>{o.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {relationsRefreshed > 0 && (
                <div className="rounded-md border bg-violet-500/5 p-3">
                  <p className="text-xs uppercase tracking-wide font-semibold text-violet-700 mb-1">
                    thematic_node_relations
                  </p>
                  <p className="text-xs">
                    {relationsRefreshed} hub_to_spoke relations utworzonych (graf widzi pillar w klastrze).
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button onClick={onClose}>Zamknij</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
